import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  FiArrowLeft, FiSave, FiLoader, FiFileText, FiExternalLink, 
  FiClock, FiXCircle, FiCheckCircle, FiCpu, FiCheck, 
  FiAlertCircle, FiDownload, FiMail, FiEdit, FiSkipForward,
  FiEye, FiEyeOff, FiRefreshCw, FiColumns, FiPlus, FiTrash2
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import { jobApi, resumeApi, applicationApi } from '../../services/api';
import ResumeComparison from '../../pages/resumes/ResumeComparison';
import VersionEditor from '../resumes/VersionEditor';
import LoadingSpinner from '../../components/LoadingSpinner';
import { DisabledCreditButton } from '../../components/membership';
import CreditCostNotification from '../../components/membership/CreditCostNotification';
import { useMembership } from '../../contexts/MembershipContext';
import { useBalance } from '../../contexts/BalanceContext';
import { useFeedback } from '../../contexts/FeedbackContext';
import { useCache } from '../../contexts/CacheContext';
import tourService from '../../services/tourService';
import ResumeStyleModal from '../../components/resumes/ResumeStyleModal';
import PageHeader from '../../components/layout/PageHeader';
import { pageDescriptions } from '../../data/pageDescriptions';
import sanitizeHtml from '../../utils/sanitizeHtml';

import '../../styles/analysis.css';
import ResumeDocument from '../resumes/ResumeDocument';

const JobApplication = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const { refreshCreditBalance } = useMembership();
  const { SERVICE_COSTS } = useBalance();
  const { triggerFeedbackNotification } = useFeedback();
  const { invalidateApplicationCache } = useCache();
  
  // State management
  const [job, setJob] = useState(null);
  const [resumes, setResumes] = useState([]);
  const [coverLetters, setCoverLetters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [selectedResume, setSelectedResume] = useState('');
  const [selectedCoverLetter, setSelectedCoverLetter] = useState('');
  const [notes, setNotes] = useState('');
  const [additionalInfo, setAdditionalInfo] = useState('');
  const [followUpQuestions, setFollowUpQuestions] = useState([]);
  const [followUpAnswers, setFollowUpAnswers] = useState({});
  const [followUpCompanySelections, setFollowUpCompanySelections] = useState({});
  const [followUpSelectedItems, setFollowUpSelectedItems] = useState({});
  const [followUpSkillsOptions, setFollowUpSkillsOptions] = useState({});
  const [showFollowUpQuestions, setShowFollowUpQuestions] = useState(false);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [compatibility, setCompatibility] = useState(null);
  const [applicationSaved, setApplicationSaved] = useState(false);
  const [alreadyAppliedModal, setAlreadyAppliedModal] = useState(false);
  const [cachedMatchInfo, setCachedMatchInfo] = useState(null); // {hasCachedAnalysis, resumeId, versionId, cachedAt}
  
  // New state for resume polishing
  const [polishing, setPolishing] = useState(false);
  const [polishResults, setPolishResults] = useState(null);
  const [originalResumeContent, setOriginalResumeContent] = useState(null);
  const [polishedResumeContent, setPolishedResumeContent] = useState(null);
  const [generatedCoverLetter, setGeneratedCoverLetter] = useState(null);
  const [activeTab, setActiveTab] = useState(1);
  const [editedResume, setEditedResume] = useState(null);
  const [editedCoverLetter, setEditedCoverLetter] = useState(null);
  const [selectedSections, setSelectedSections] = useState(['summary', 'skills', 'experience']);
  const [showResumeStyleModal, setShowResumeStyleModal] = useState(false);
  const [showLastMinuteModal, setShowLastMinuteModal] = useState(false);
  const [showCoverLetterDownloadModal, setShowCoverLetterDownloadModal] = useState(false);
  const [coverLetterDownloadFileName, setCoverLetterDownloadFileName] = useState('');
  const [coverLetterPdfDownloading, setCoverLetterPdfDownloading] = useState(false);
  const lastMinuteEditorRef = useRef(null);
  /** Prevents duplicate analyzeJobMatch calls (auto-load + Strict Mode / rapid re-renders). */
  const analyzeInFlightRef = useRef(false);
  /** One auto-load of cached analysis per job+resume selection. */
  const autoCachedAnalysisTriggeredRef = useRef(false);
  
  // New state for generation options
  const [generateResume, setGenerateResume] = useState(true);
  const [generateCoverLetter, setGenerateCoverLetter] = useState(true);
  // Cover letter customization
  const [coverLetterWordCount, setCoverLetterWordCount] = useState(350);
  const [coverLetterWordCountInput, setCoverLetterWordCountInput] = useState('350');
  const [coverLetterCustomInstruction, setCoverLetterCustomInstruction] = useState('');
  // Resume customization
  const [resumeRemoveSummary, setResumeRemoveSummary] = useState(false);
  const [resumeCustomInstructions, setResumeCustomInstructions] = useState({ summary: '', experience: '', skills: '' });
  const [experienceBulletLimits, setExperienceBulletLimits] = useState([]); // null = keep all, number = max bullets per job
  const [experienceFilterMode, setExperienceFilterMode] = useState('keep_all'); // 'keep_all' | 'ai_relevant_only'
  // Resume content loaded for Step 3 (job list, bullet counts) before generation
  const [step3ResumeContent, setStep3ResumeContent] = useState(null);
  
  // Step management
  const [currentStep, setCurrentStep] = useState(1);
  // Step 1: Job details & resume selection
  // Step 2: Compatibility analysis & Additional information
  // Step 3: Polish resume & generate cover letter
  // Step 4: Review & compare
  // Step 5: Download & apply
  
  // Fetching data
  useEffect(() => {
    fetchData();
  }, [jobId]);
  
  // Check for cached match analysis when resume or job changes
  useEffect(() => {
    if (!selectedResume || !jobId) {
      setCachedMatchInfo(null);
      return;
    }
    let cancelled = false;
    jobApi.checkCachedMatch(jobId, selectedResume)
      .then((res) => {
        if (cancelled) return;
        if (res?.data?.hasCachedAnalysis) {
          setCachedMatchInfo(res.data);
        } else {
          setCachedMatchInfo(null);
        }
      })
      .catch((err) => {
        if (cancelled) return;
        console.log('No cached match found');
        setCachedMatchInfo(null);
      });
    return () => { cancelled = true; };
  }, [selectedResume, jobId]);

  useEffect(() => {
    autoCachedAnalysisTriggeredRef.current = false;
  }, [jobId, selectedResume]);
  
  // Start tour when component mounts
  useEffect(() => {
    if (!tourService.isTourCompleted('job_application')) {
      setTimeout(() => {
        tourService.startTour('job_application');
      }, 1000);
    }
  }, []);
  
  // Backend automatically uses first version, no need to manage versions
  
  // Load resume content when Step 3 is active so we can show job list and bullet options
  useEffect(() => {
    if (currentStep !== 3 || !selectedResume) {
      return;
    }
    let cancelled = false;
    resumeApi.getFirstResumeVersion(selectedResume)
      .then((res) => {
        if (cancelled || !res?.data?.content) return;
        const content = res.data.content;
        setStep3ResumeContent(content);
        // Initialize experience bullet limits to "keep all" (null) for each job
        if (content.experience && Array.isArray(content.experience)) {
          setExperienceBulletLimits((prev) => {
            if (prev.length === content.experience.length) return prev;
            return content.experience.map(() => null);
          });
        }
      })
      .catch((err) => {
        console.error('Error loading resume for Step 3:', err);
        toast.error('Failed to load resume content. Please try again.');
      });
    return () => { cancelled = true; };
  }, [currentStep, selectedResume]);

  // Effect to update editedResume when polishedResumeContent or originalResumeContent changes
  useEffect(() => {
    if (generateResume && polishedResumeContent) {
      setEditedResume(polishedResumeContent);
    } else if (!generateResume && originalResumeContent) {
      setEditedResume(originalResumeContent);
    }
  }, [polishedResumeContent, originalResumeContent, generateResume]);
  


  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch job details
      const jobResponse = await jobApi.getJobById(jobId);
      setJob(jobResponse.data);
      
      // Store job content for direct passing
      if (jobResponse.data && jobResponse.data.content) {
      }
      
      // Fetch user resumes
      const resumesResponse = await resumeApi.getAllResumes();
      setResumes(resumesResponse.data);
      
      if (resumesResponse.data.length > 0) {
        setSelectedResume(resumesResponse.data[0].id);
        // Backend will automatically use first version
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load application data');
    } finally {
      setLoading(false);
    }
  };
  
  
  const handleResumeChange = async (e) => {
    const resumeId = e.target.value;
    setSelectedResume(resumeId);
    // Reset compatibility analysis when resume changes
    setCompatibility(null);
    // Reset current step
    setCurrentStep(1);
  };
  
  const analyzeCompatibility = async (options = {}) => {
    const fromAutoLoad = options.fromAutoLoad === true;
    if (!selectedResume) {
      toast.error('Please select a resume');
      return;
    }
    if (analyzeInFlightRef.current) {
      return;
    }
    analyzeInFlightRef.current = true;
    if (fromAutoLoad) {
      autoCachedAnalysisTriggeredRef.current = true;
    }

    try {
      setSubmitting(true);
      
      // Backend will automatically use the first version
      
      // Get first version's content directly
      let resumeContent = null;
      try {
        const firstVersionResponse = await resumeApi.getFirstResumeVersion(selectedResume);
        if (firstVersionResponse.data && firstVersionResponse.data.content) {
          resumeContent = firstVersionResponse.data.content;
          // Store the resume content for use in follow-up questions
          setOriginalResumeContent(resumeContent);
        }
      } catch (error) {
        console.error('Error fetching first version content:', error);
      }
      
      let jobContent = null;
      if (job) {
        // Log the job object to see its structure
        
        // The job content might be the job object itself, not in a content property
        jobContent = job;
      }
      
      
      // Call the backend API to analyze the match with direct content passing
      const response = await jobApi.analyzeJobMatch(
        jobId, 
        selectedResume, 
        null, // Backend will use first version automatically
        resumeContent,
        jobContent
      );
      
      // The response will contain HTML content and follow-up questions
      const htmlAnalysis = response.data.htmlAnalysis;
      const followUpQuestions = response.data.followUpQuestions || [];
      const cachedAdditionalInfo = response.data.additionalInfo || '';
      const cachedFollowUpAnswers = response.data.followUpAnswers || {};
      const isCached = response.data.cached;
      
      // Set this for display, adding a property to store the HTML
      const result = {
        htmlContent: htmlAnalysis,
        // Extract an approximate overall score from the HTML
        // This is a simple regex to get a number after "Overall Match Score" text
        overallScore: parseInt(
          htmlAnalysis.match(/Overall Match Score[^0-9]*([0-9]+)/i)?.[1] || "70"
        )
      };
      
      // Store follow-up questions and handle answers appropriately
      setFollowUpQuestions(followUpQuestions);
      
      if (isCached && cachedFollowUpAnswers && Object.keys(cachedFollowUpAnswers).length > 0) {
        // If this is cached analysis with previous answers, restore them
        setAdditionalInfo(cachedAdditionalInfo);
        setFollowUpAnswers(cachedFollowUpAnswers);
        // Determine if follow-up questions should be shown based on whether answers exist
        const hasAnswers = Object.keys(cachedFollowUpAnswers).some(key => cachedFollowUpAnswers[key]?.trim());
        setShowFollowUpQuestions(hasAnswers);
        setCurrentQuestionIndex(0);
        // Reset other state that may not be saved
        setFollowUpCompanySelections({});
        setFollowUpSelectedItems({});
        setFollowUpSkillsOptions({});
      } else {
        // Fresh analysis or cached without answers - reset everything
        setFollowUpAnswers({});
        setFollowUpCompanySelections({});
        setFollowUpSelectedItems({});
        setFollowUpSkillsOptions({});
        setShowFollowUpQuestions(false);
        setCurrentQuestionIndex(0);
        setAdditionalInfo('');
      }
      
      // Refresh credit balance after successful analysis and update UI immediately
      const updatedBalance = await refreshCreditBalance();
      
      setCompatibility(result);
      // Move to step 2 after analysis - this stays the same
      setCurrentStep(2);
    } catch (error) {
      console.error('Error analyzing compatibility:', error);
      if (error.response && error.response.status === 402) {
        toast.error('Not enough credits. Please upgrade your plan or purchase more credits.');
      } else {
        toast.error('Failed to analyze resume compatibility');
      }
    } finally {
      setSubmitting(false);
      analyzeInFlightRef.current = false;
    }
  };

  useEffect(() => {
    if (loading) return;
    if (!job || !selectedResume) return;
    if (!cachedMatchInfo?.hasCachedAnalysis) return;
    if (
      cachedMatchInfo.resumeId != null &&
      String(cachedMatchInfo.resumeId) !== String(selectedResume)
    ) {
      return;
    }
    if (compatibility) return;
    if (currentStep !== 1) return;
    if (autoCachedAnalysisTriggeredRef.current) return;

    analyzeCompatibility({ fromAutoLoad: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: run when cache info is ready, not on every analyzeCompatibility identity
  }, [
    loading,
    job,
    selectedResume,
    cachedMatchInfo?.hasCachedAnalysis,
    compatibility,
    currentStep,
  ]);
  
  // New function to handle generation based on user selections
  const handleGenerate = async () => {
    if (!selectedResume) {
      toast.error('Please select a resume first');
      return;
    }
    
    if (!generateResume && !generateCoverLetter) {
      toast.error('Please select at least one option to generate');
      return;
    }
    
    // Only proceed if we have job data
    if (!job) {
      toast.error('Job data not available. Please try again.');
      return;
    }
    
    // Set initial active tab based on what's being generated
    if (generateResume) {
      setActiveTab(1); // Compare Changes tab
    } else if (generateCoverLetter) {
      setActiveTab(2); // Cover Letter tab
    }
    
    try {
      setPolishing(true);
      
      // Initialize these states to avoid undefined errors
      setEditedResume(null);
      setEditedCoverLetter(null);
      
      // Store the original resume content for comparison
      try {
        // Get the first version content from API (backend will use first version automatically)
        const firstVersionResponse = await resumeApi.getFirstResumeVersion(selectedResume);
        if (firstVersionResponse && firstVersionResponse.data && firstVersionResponse.data.content) {
          const foundContent = firstVersionResponse.data.content;
          setOriginalResumeContent(foundContent);
        }
      } catch (err) {
        console.error('Error fetching original resume content:', err);
      }
      
      // Backend will automatically use the first version
      
      // Format job description
      const jobDescription = [
        `Title: ${job.title}`,
        `Company: ${job.company}`,
        job.responsibilities ? `Responsibilities: ${job.responsibilities.join('. ')}` : '',
        job.requirements ? `Requirements: ${job.requirements.join('. ')}` : '',
        job.skills ? `Skills: ${job.skills.join(', ')}` : ''
      ].filter(Boolean).join('\\n\\n');
      
      // Get resume content for direct passing
      let resumeContent = originalResumeContent;
      
      // If we still don't have content, try one more time to fetch it
      if (!resumeContent) {
        try {
          const contentResponse = await resumeApi.getFirstResumeVersion(selectedResume);
          if (contentResponse && contentResponse.data && contentResponse.data.content) {
            resumeContent = contentResponse.data.content;
            // Also update the state
            setOriginalResumeContent(resumeContent);
          }
        } catch (err) {
          console.error('Error fetching resume content:', err);
        }
      } else {
      }
      
      // Create promises array for parallel execution
      const promises = [];
      
      // If user wants to polish resume, add the promise
      if (generateResume) {
        const sections = selectedSections.length > 0 ? selectedSections : ['summary', 'skills', 'experience'];
        const expList = resumeContent?.experience || [];
        const bulletLimits = expList.length > 0
          ? expList.map((_, i) => experienceBulletLimits[i] ?? null)
          : undefined;
        const polishPromise = resumeApi.polishResume(
          selectedResume,
          sections,
          jobDescription,
          `Tailored for ${job.company} - ${job.title}`,
          formatAdditionalInfo(),
          resumeContent,
          null, // experienceIndexes
          {
            cost: SERVICE_COSTS['resume_polish'],
            removeSummary: resumeRemoveSummary,
            sectionCustomInstructions: resumeCustomInstructions,
            experienceBulletLimits: bulletLimits,
            experienceFilterMode: experienceFilterMode,
          }
        );
        promises.push(polishPromise);
      }

      // If user wants to generate cover letter, add the promise
      // Always use the original resume content for true parallelism
      if (generateCoverLetter) {
        const n = Number(coverLetterWordCountInput);
        const effectiveWordCount = Number.isNaN(n) ? coverLetterWordCount : Math.min(800, Math.max(150, n));
        const coverLetterPromise = jobApi.generateCoverLetter(
          jobId,
          selectedResume,
          null, // Backend will use first version automatically
          formatAdditionalInfo(),
          resumeContent, // Always use original content for parallel execution
          job,
          {
            cost: SERVICE_COSTS['cover_letter'],
            wordCount: effectiveWordCount,
            customInstruction: coverLetterCustomInstruction || undefined,
          }
        );
        promises.push(coverLetterPromise);
      }
      
      // Execute all promises in parallel
      const results = await Promise.all(promises);
      
      // Process results
      let resultIndex = 0;
      
      // Process resume polish result if it exists
      if (generateResume) {
        const polishResponse = results[resultIndex++];
        const polishedVersionId = polishResponse.data?.versionId || polishResponse.versionId;
        
        // Store the results
        setPolishResults(polishResponse.data.content);
        
        // Store the polished resume content
        setPolishedResumeContent({
          ...polishResponse.data.content,
          versionId: polishedVersionId,
          resumeId: selectedResume
        });
        
        // Note: Backend automatically uses first version
      }
      
      // Process cover letter result if it exists
      if (generateCoverLetter) {
        const coverLetterResponse = results[resultIndex++]; // Get the cover letter response
        
        // Safely access data properties with fallbacks
        const coverLetterContent = coverLetterResponse?.data?.coverLetter || coverLetterResponse?.coverLetter;
        const coverLetterId = coverLetterResponse?.data?.coverLetterId || coverLetterResponse?.coverLetterId;
        
        setGeneratedCoverLetter(coverLetterContent);
        
        if (coverLetterContent) {
          setEditedCoverLetter({
            id: coverLetterId,
            content: coverLetterContent
          });
        }
        
        if (coverLetterId) {
          setSelectedCoverLetter(coverLetterId);
        }
      }
      
      // Refresh credit balance after successful operations
      await refreshCreditBalance();
      
      // Move to step 4 after processing
      setCurrentStep(4);
      
      // Show success message
      if (generateResume && generateCoverLetter) {
        toast.success('Resume polished and cover letter generated successfully!');
      } else if (generateResume) {
        toast.success('Resume polished successfully!');
      } else if (generateCoverLetter) {
        toast.success('Cover letter generated successfully!');
      }
      if (generateResume && followUpQuestions.length > 0 && Object.keys(followUpAnswers).some((k) => followUpAnswers[k]?.trim())) {
        toast.success('Your answers to the follow-up questions were included in the resume tailoring.');
      }
    } catch (error) {
      console.error('Error during generation:', error);
      if (error.response && error.response.status === 402) {
        toast.error('Not enough credits. Please upgrade your plan or purchase more credits.');
      } else {
        toast.error('Failed to complete the requested operation');
      }
    } finally {
      setPolishing(false);
    }
  };
  
  // Keep the old function for backward compatibility
  const handlePolishResume = handleGenerate;
  
  const handleSaveCoverLetterEdits = async () => {
    try {
      setSubmitting(true);
      
      // Process the cover letter body into paragraphs
      let coverLetterToSave = {...editedCoverLetter};
      
      if (editedCoverLetter?.content?.body?.[0]) {
        // Get the raw text from the first element of the body array
        const rawText = editedCoverLetter.content.body[0];
        
        // Split by newlines and filter out empty lines
        const paragraphs = rawText
          .split('\n')
          .map(p => p.trim())
          .filter(p => p.length > 0);
        
        // Update the body with processed paragraphs
        coverLetterToSave = {
          ...coverLetterToSave,
          content: {
            ...coverLetterToSave.content,
            body: paragraphs.length > 0 ? paragraphs : ['']
          }
        };
      }
      
      // Save cover letter edits
      if (coverLetterToSave && selectedCoverLetter) {
        await jobApi.updateCoverLetter(
          selectedCoverLetter,
          coverLetterToSave
        );
      } else if (coverLetterToSave && generatedCoverLetter) {
        // If we have edited cover letter content but no selected ID, use the generated one's ID
        await jobApi.updateCoverLetter(
          generatedCoverLetter.id,
          coverLetterToSave
        );
      }
      
      toast.success('Changes saved successfully!');
      //setCurrentStep(5); // Move to the next step after saving
    } catch (error) {
      console.error('Error saving edits:', error);
      toast.error('Failed to save changes');
    } finally {
      setSubmitting(false);
    }
  };
  

  const handleCoverLetterEdit = (field, value) => {
    setEditedCoverLetter(prev => {
      const updatedCoverLetter = prev || {};
      return { ...updatedCoverLetter, [field]: value };
    });
  };

  // Full cover letter as single text (for display in one textarea)
  const getCoverLetterFullText = () => {
    const content = editedCoverLetter?.content || generatedCoverLetter?.content || {};
    const parts = [
      content.greeting,
      content.introduction,
      ...(Array.isArray(content.body) ? content.body : [content.body].filter(Boolean)),
      content.conclusion,
      content.signature
    ].filter(Boolean);
    return parts.join('\n\n');
  };

  const handleCoverLetterFullTextChange = (fullText) => {
    const content = editedCoverLetter?.content || generatedCoverLetter?.content || {};
    handleCoverLetterEdit('content', {
      ...content,
      greeting: '',
      introduction: '',
      body: [fullText],
      conclusion: '',
      signature: ''
    });
  };
  
  // Extract companies and projects from resume experience data
  const getCompaniesAndProjectsFromResume = () => {
    if (!originalResumeContent || !originalResumeContent.experience) {
      return [];
    }
    
    
    const items = [];
    originalResumeContent.experience.forEach((exp, index) => {
      
      // Add company
      if (exp.company) {
        items.push({
          type: 'company',
          name: exp.company,
          displayName: exp.company
        });
      }
      
      // Add projects if they exist and has_projects is true
      if (exp.has_projects && exp.projects && Array.isArray(exp.projects)) {
        exp.projects.forEach(project => {
          if (project.title) {
            const companyContext = exp.company ? ` (${exp.company})` : '';
            items.push({
              type: 'project',
              name: project.title,
              displayName: `${project.title}${companyContext}`,
              company: exp.company
            });
          }
        });
      }
    });
    
    
    // Sort by type (companies first) then by name
    return items.sort((a, b) => {
      if (a.type !== b.type) {
        return a.type === 'company' ? -1 : 1;
      }
      return a.name.localeCompare(b.name);
    });
  };
  
  // Handle follow-up question answers
  const handleFollowUpAnswer = (questionIndex, answer) => {
    setFollowUpAnswers(prev => ({
      ...prev,
      [questionIndex]: answer
    }));
  };
  
  // Handle company/project selection for follow-up questions
  const handleFollowUpCompanySelection = (questionIndex, selectedValue) => {
    setFollowUpCompanySelections(prev => ({
      ...prev,
      [questionIndex]: selectedValue
    }));
    
    // Also store the full item details for formatting
    if (selectedValue) {
      const items = getCompaniesAndProjectsFromResume();
      const selectedItem = items.find(item => item.displayName === selectedValue);
      setFollowUpSelectedItems(prev => ({
        ...prev,
        [questionIndex]: selectedItem
      }));
    } else {
      setFollowUpSelectedItems(prev => {
        const newItems = { ...prev };
        delete newItems[questionIndex];
        return newItems;
      });
    }
  };
  
  // Handle skills option for follow-up questions
  const handleFollowUpSkillsOption = (questionIndex, addToSkills) => {
    setFollowUpSkillsOptions(prev => ({
      ...prev,
      [questionIndex]: addToSkills
    }));
  };
  
  // Handle question navigation
  const handleNextQuestion = () => {
    if (currentQuestionIndex < followUpQuestions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    }
  };
  
  const handlePreviousQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
    }
  };
  
  const handleShowFollowUpQuestions = () => {
    setShowFollowUpQuestions(true);
    setCurrentQuestionIndex(0);
  };
  
  const handleHideFollowUpQuestions = () => {
    setShowFollowUpQuestions(false);
    setCurrentQuestionIndex(0);
  };
  
  // Format follow-up questions and answers into additional information
  const formatAdditionalInfo = () => {
    let formattedInfo = additionalInfo;
    
    // Add follow-up questions and answers if they exist
    if (followUpQuestions.length > 0) {
      const followUpSection = followUpQuestions
        .map((question, index) => {
          const answer = followUpAnswers[index] || '';
          const selectedItem = followUpSelectedItems[index];
          const addToSkills = followUpSkillsOptions[index];
          
          if (answer.trim()) {
            let formattedAnswer = answer;
            
            // Add company/project context if selected
            if (selectedItem) {
              if (selectedItem.type === 'company') {
                formattedAnswer = `At ${selectedItem.name}, ${answer.toLowerCase()}`;
              } else if (selectedItem.type === 'project') {
                const projectContext = selectedItem.company 
                  ? `During the ${selectedItem.name} project at ${selectedItem.company}`
                  : `During the ${selectedItem.name} project`;
                formattedAnswer = `${projectContext}, ${answer.toLowerCase()}`;
              }
            }
            
            // Add skills note if selected
            if (addToSkills) {
              formattedAnswer += ' (This experience should be added to skills section)';
            }
            
            return `Q: ${question}\nA: ${formattedAnswer}`;
          }
          return null;
        })
        .filter(Boolean)
        .join('\n\n');
      
      if (followUpSection) {
        formattedInfo = formattedInfo 
          ? `${formattedInfo}\n\n--- Follow-up Questions ---\n\n${followUpSection}`
          : `--- Follow-up Questions ---\n\n${followUpSection}`;
      }
    }
    
    return formattedInfo;
  };
  
  
  
  // Build short job title for filenames (before first colon or comma; only remove unsafe chars, keep spaces)
  const getShortJobTitleForFilename = (titleStr, maxLen = 40) => {
    if (!titleStr || typeof titleStr !== 'string') return 'Job';
    let short = titleStr.trim();
    if (titleStr.includes(':')) short = titleStr.split(':')[0].trim();
    else if (titleStr.includes(',')) short = titleStr.split(',')[0].trim();
    const safe = short.replace(/[\\/:*?"<>|]/g, '').replace(/\s+/g, ' ').trim();
    return (safe.slice(0, maxLen) || 'Job');
  };
  // Title case for names (e.g. 'QI ZHAO' -> 'Qi Zhao') for display and filenames
  const toTitleCaseName = (str) => {
    if (!str || typeof str !== 'string') return str || '';
    return str.trim().split(/\s+/).map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');
  };
  const getSuggestedCoverLetterFileName = () => {
    const sig = editedCoverLetter?.content?.signature || generatedCoverLetter?.signature;
    const nameLine = (typeof sig === 'string' && sig.includes('\n')) ? sig.split('\n').pop().trim() : (sig || '');
    const raw = (nameLine || polishedResumeContent?.name || 'Document').replace(/[\\/:*?"<>|]/g, '').replace(/\s+/g, ' ').trim();
    const namePart = toTitleCaseName(raw) || 'Document';
    const jobPart = job ? getShortJobTitleForFilename(job.shortTitle || job.title) : 'Job';
    return `${namePart} - Cover Letter for ${jobPart}.pdf`;
  };
  const getSuggestedResumeFileName = () => {
    const raw = (polishedResumeContent?.name || originalResumeContent?.name || 'Resume').replace(/[\\/:*?"<>|]/g, '').replace(/\s+/g, ' ').trim();
    const name = toTitleCaseName(raw) || 'Resume';
    const jobPart = job ? getShortJobTitleForFilename(job.shortTitle || job.title) : 'Job';
    return `${name} - Resume for ${jobPart}.pdf`;
  };

  const openCoverLetterDownloadModal = () => {
    if (!selectedCoverLetter) {
      toast.error('Please select a cover letter first');
      return;
    }
    setCoverLetterDownloadFileName(getSuggestedCoverLetterFileName());
    setShowCoverLetterDownloadModal(true);
  };

  const generateCoverLetterPDF = async (fileName = null) => {
    if (!selectedCoverLetter) {
      toast.error('Please select a cover letter first');
      return;
    }
    const options = (fileName != null && String(fileName).trim()) ? { fileName: String(fileName).trim() } : {};
    try {
      setCoverLetterPdfDownloading(true);
      await applicationApi.generateCoverLetterPDF(selectedCoverLetter, options);
      setShowCoverLetterDownloadModal(false);
      toast.success('Cover letter PDF generated successfully');
    } catch (error) {
      console.error('Error generating cover letter PDF:', error);
      toast.error('Failed to generate cover letter PDF');
    } finally {
      setCoverLetterPdfDownloading(false);
    }
  };
  
  const handleSaveApplication = async () => {
    if (!selectedResume) {
      toast.error('Please select a resume');
      return;
    }
    
    // Backend will automatically use the first version
    
    try {
      setSubmitting(true);
      
      // Use the jobApi method; pass polished version if user generated one, else backend uses first version
      const response = await jobApi.applyForJob(
        jobId,
        selectedResume,
        polishedResumeContent?.versionId || null,
        notes,
        selectedCoverLetter || null
      );
      
      
      setApplicationSaved(true);
      toast.success('Application tracked successfully!');
      
      // Invalidate application cache so new application shows up
      invalidateApplicationCache();
      
      // Trigger feedback notification for first application
      triggerFeedbackNotification();
      
      // Get the application ID from the response
      const newApplicationId = response.data?.id || response.data?.applicationId;
      
      // Delay navigation to show success message
      setTimeout(() => {
        // Navigate with the new application ID as a query parameter
        navigate(`/applications?highlight=${newApplicationId}`);
      }, 2000);
    } catch (error) {
      console.error('Error saving application:', error);
      
      // Check if this is the "already applied" error
      if (error.response && 
          error.response.data && 
          error.response.data.detail && 
          error.response.data.detail.includes("already applied")) {
        // Show the already applied modal instead of a toast
        setAlreadyAppliedModal(true);
        toast.error('You have already applied this job!');
      } else {
        toast.error('Failed to save application');
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Handle skipping directly from Step 1 to Step 3 (Generation)
  const handleSkipToGeneration = async () => {
    // Ensure we have a resume selected
    if (!selectedResume) {
      toast.error('Please select a resume first');
      return;
    }
    
    // Backend will automatically use the first version
    
    // Skip the compatibility analysis step
    setCurrentStep(3);
  };

  // Handle skipping from Step 3 (Generation) to Step 5 (Apply)
  const handleSkipToApply = async () => {
    // Ensure we have a resume selected
    if (!selectedResume) {
      toast.error('Please select a resume first');
      return;
    }
    
    // Backend will automatically use the first version
    
    // Skip both generation and review steps
    setCurrentStep(5);
  };
  
  // Helper functions for generation options
  const calculateCost = () => {
    let cost = 0;
    if (generateResume) cost += SERVICE_COSTS['resume_polish'];
    if (generateCoverLetter) cost += SERVICE_COSTS['cover_letter'];
    return cost;
  };
  
  const getServiceName = () => {
    if (generateResume && generateCoverLetter) return "resume polish and cover letter generation";
    if (generateResume) return "resume polish";
    if (generateCoverLetter) return "cover letter generation";
    return "";
  };
  
  const getServiceType = () => {
    if (generateResume && generateCoverLetter) return "resume_polish";
    if (generateResume) return "resume_polish";
    if (generateCoverLetter) return "cover_letter";
    return "resume_polish";
  };
  
  const getButtonText = () => {
    if (generateResume && generateCoverLetter) return "Polish Resume & Generate Cover Letter";
    if (generateResume) return "Polish Resume";
    if (generateCoverLetter) return "Generate Cover Letter";
    return "Select an option";
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };
  
  // Function to render progress steps (modern stepper with connecting line)
  const renderProgressSteps = () => {
    const steps = [
      { id: 1, name: 'Job Details' },
      { id: 2, name: 'Analysis & Input' },
      { id: 3, name: 'Generation' },
      { id: 4, name: 'Review' },
      { id: 5, name: 'Apply' }
    ];
    const totalSteps = steps.length;

    return (
      <div className="w-full">
        <div className="flex items-start justify-between relative">
          {/* Connecting line (background) */}
          <div
            className="absolute top-5 left-[10%] right-[10%] h-0.5 bg-gray-200"
            aria-hidden
          />
          {/* Progress fill */}
          <div
            className="absolute top-5 left-[10%] h-0.5 bg-primary-500 transition-all duration-300 ease-out"
            style={{ width: `${((currentStep - 1) / (totalSteps - 1)) * 80}%` }}
            aria-hidden
          />
          {steps.map((step, index) => {
            const isCompleted = currentStep > step.id;
            const isCurrent = currentStep === step.id;
            const isUpcoming = currentStep < step.id;
            return (
              <div key={step.id} className="flex flex-col items-center relative z-10 flex-1">
                <div
                  className={`flex items-center justify-center w-10 h-10 rounded-full border-2 transition-all duration-200 ${
                    isCompleted
                      ? 'bg-primary-500 border-primary-500 text-white'
                      : isCurrent
                        ? 'bg-white border-primary-500 text-primary-600 ring-4 ring-primary-100'
                        : 'bg-white border-gray-200 text-gray-400'
                  }`}
                >
                  {isCompleted ? (
                    <FiCheck className="w-5 h-5" strokeWidth={2.5} />
                  ) : (
                    <span className="text-sm font-semibold">{step.id}</span>
                  )}
                </div>
                <span
                  className={`mt-2 text-xs font-medium text-center max-w-[4.5rem] sm:max-w-none ${
                    isCurrent ? 'text-primary-600' : isCompleted ? 'text-gray-700' : 'text-gray-400'
                  }`}
                  style={{ whiteSpace: 'nowrap' }}
                >
                  {step.name}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  if (loading) {
    return <LoadingSpinner message="Loading job application data..." />;
  }
  
  if (!job) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold text-gray-900">Job not found</h2>
        <p className="mt-2 text-gray-600">The job you're trying to apply for does not exist.</p>
        <div className="mt-6">
          <Link
            to="/jobs"
            className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <FiArrowLeft className="-ml-1 mr-2 h-5 w-5" />
            Back to Jobs
          </Link>
        </div>
      </div>
    );
  }
  
  return (
    <div className="max-w-6xl mx-auto">
      <Link
        to="/jobs"
        className="inline-flex items-center text-sm text-gray-500 hover:text-primary-600 transition-colors mb-6"
      >
        <FiArrowLeft className="mr-2 h-4 w-4" />
        Back to Jobs
      </Link>

      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden mb-6">
        <div className="px-6 sm:px-8 pt-6 pb-2">
          <PageHeader
            title={pageDescriptions.jobApplication.title}
            description={pageDescriptions.jobApplication.description}
            whatYouCanDo={pageDescriptions.jobApplication.whatYouCanDo}
            whatYouShouldDo={pageDescriptions.jobApplication.whatYouShouldDo}
          />
          <p className="mt-1 text-sm text-gray-500">
            {job.title}
            {job.company && <span className="text-gray-400"> · {job.company}</span>}
          </p>
        </div>

        {/* Progress stepper */}
        <div className="px-6 sm:px-8 py-6 border-b border-gray-100">
          {renderProgressSteps()}
        </div>

        <div className="px-6 sm:px-8 py-6">
          <div className="space-y-6">
            {/* Job Info - compact card */}
            <div className="rounded-xl border border-gray-100 bg-gray-50/50 p-4 sm:p-5">
              <h3 className="text-base font-semibold text-gray-900">{job.title}</h3>
              <p className="mt-0.5 text-sm text-gray-600">{job.company}</p>
              <p className="mt-1 text-xs text-gray-500">Added on {formatDate(job.createdAt)}</p>
              
              {job.location && (
                <div className="mt-2 flex items-center">
                  <span className="text-sm font-medium text-gray-500">Location:</span>
                  <span className="ml-2 text-sm text-gray-700">{job.location}</span>
                </div>
              )}

              {job.employment_type && (
                <div className="mt-1 flex items-center">
                  <span className="text-sm font-medium text-gray-500">Type:</span>
                  <span className="ml-2 text-sm text-gray-700">{job.employment_type}</span>
                </div>
              )}

              {job.salary_range && (
                <div className="mt-1 flex items-center">
                  <span className="text-sm font-medium text-gray-500">Salary Range:</span>
                  <span className="ml-2 text-sm text-gray-700">{job.salary_range}</span>
                </div>
              )}

              {job.summary && (
                <div className="mt-4">
                  <h4 className="text-sm font-medium text-gray-700">Summary</h4>
                  <p className="mt-1 text-sm text-gray-600">{job.summary}</p>
                </div>
              )}
              
              {job.skills && job.skills.length > 0 && (
                <div className="mt-4">
                  <h4 className="text-sm font-medium text-primary-800">Required Skills</h4>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {job.skills.map((skill, index) => (
                      <span key={index} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              
              {job.sourceUrl && (
                <div className="mt-4 flex justify-center">
                  <a
                    href={job.sourceUrl}
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  >
                    <FiExternalLink className="-ml-1 mr-2 h-4 w-4" />
                    View Job Posting
                  </a>
                </div>
              )}
            </div>
            
            {/* Step 1: Resume Selection */}
            {currentStep === 1 && (
              <div className="space-y-6">
                <h3 className="text-lg font-medium text-gray-900">Step 1: Select Your Resume</h3>
                
                <div>
                  <label htmlFor="resume" className="block text-sm font-medium text-gray-700">
                    Select Resume
                  </label>
                  {resumes.length > 0 ? (
                    <div className="mt-1">
                      <select
                        id="resume"
                        name="resume"
                        className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md"
                        value={selectedResume}
                        onChange={handleResumeChange}
                      >
                        {resumes.map(resume => (
                          <option key={resume.id} value={resume.id}>
                            {resume.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  ) : (
                    <div className="mt-1 text-sm text-red-600">
                      You don't have any resumes yet. Please upload a resume first.
                      <div className="mt-2">
                        <Link
                          to="/resumes/upload"
                          className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-primary-700 bg-primary-100 hover:bg-primary-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                        >
                          <FiFileText className="-ml-0.5 mr-2 h-4 w-4" />
                          Upload Resume
                        </Link>
                      </div>
                    </div>
                  )}
                </div>
                
                
                <div className="space-y-4">
                  <CreditCostNotification serviceCost={SERVICE_COSTS['job_matching']} serviceName="job match analysis" />
                  
                  {cachedMatchInfo?.hasCachedAnalysis && (
                    <div className="p-3 bg-green-50 border border-green-200 rounded-md">
                      <div className="flex">
                        <div className="flex-shrink-0">
                          <FiCheck className="h-5 w-5 text-green-400" />
                        </div>
                        <div className="ml-3">
                          <p className="text-sm font-medium text-green-800">
                            Using cached analysis from this resume
                          </p>
                          {cachedMatchInfo.cachedAt && (
                            <p className="text-xs text-green-600 mt-1">
                              Analyzed on {new Date(cachedMatchInfo.cachedAt).toLocaleString()}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <div className="flex justify-end space-x-3">
                    {/* Added Skip button */}
                    <button
                      type="button"
                      className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                      onClick={handleSkipToGeneration}
                      disabled={!selectedResume}
                    >
                      <FiSkipForward className="-ml-1 mr-2 h-4 w-4" />
                      Skip to Generation
                    </button>
                    
                    <DisabledCreditButton
                      serviceType="job_matching"
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                      onClick={analyzeCompatibility}
                      disabled={!selectedResume || submitting}
                    >
                      {submitting ? (
                        <>
                          <FiLoader className="animate-spin -ml-1 mr-2 h-4 w-4" />
                          Analyzing...
                        </>
                      ) : cachedMatchInfo?.hasCachedAnalysis ? (
                        <>
                          <FiCheck className="-ml-1 mr-2 h-4 w-4" />
                          Show Cached Analysis (Free)
                        </>
                      ) : (
                        <>
                          <FiCpu className="-ml-1 mr-2 h-4 w-4" />
                          Analyze Compatibility ({SERVICE_COSTS['job_matching']} credits)
                        </>
                      )}
                    </DisabledCreditButton>
                  </div>
                </div>
              </div>
            )}
            
            
            {/* Step 2: Combined Compatibility Analysis and Additional Information */}
            {currentStep === 2 && compatibility && (
              <div className="space-y-6">
                <h3 className="text-lg font-medium text-gray-900">Step 2: Resume Analysis & Additional Information</h3>
                
                {/* Compatibility Analysis Section */}
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <div 
                    className="compatibility-analysis"
                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(compatibility.htmlContent) }}
                  />
                </div>
                
                <CreditCostNotification serviceCost={SERVICE_COSTS['resume_polish']} serviceName="resume polish and cover letter generation" />

                {/* Help us tailor: one card for follow-up + free-text */}
                <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-4">
                  <div>
                    <h4 className="text-base font-medium text-gray-900">Help us tailor your resume and cover letter</h4>
                    <p className="mt-1 text-sm text-gray-600">
                      The information you add here is used only for this job application. It helps the AI strengthen your resume and cover letter with your specific examples.
                    </p>
                  </div>
                  <div className="bg-amber-50 p-3 rounded-md border border-amber-100">
                    <p className="text-sm text-amber-800">
                      Address gaps or strengthen matches from the analysis above. Optional but recommended for better results.
                    </p>
                    {(additionalInfo.trim() || Object.keys(followUpAnswers).some(key => followUpAnswers[key]?.trim())) && (
                      <div className="mt-2 flex items-center text-sm text-green-700">
                        <FiCheckCircle className="mr-1 h-4 w-4 flex-shrink-0" />
                        Previous additional information has been restored from your last session.
                      </div>
                    )}
                  </div>

                  {/* Follow-up Questions Section */}
                  {followUpQuestions.length > 0 && (
                    <div>
                      {!showFollowUpQuestions ? (
                        <div className="text-center py-2">
                        <h4 className="text-sm font-medium text-gray-700 mb-2">
                          Want to improve your resume with targeted questions?
                        </h4>
                        <p className="text-sm text-gray-600 mb-3">
                          Answer {followUpQuestions.length} follow-up question{followUpQuestions.length > 1 ? 's' : ''} to provide more context about your qualifications.
                        </p>
                        <button
                          type="button"
                          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                          onClick={handleShowFollowUpQuestions}
                        >
                          <FiPlus className="-ml-1 mr-2 h-4 w-4" />
                          Add More Information
                        </button>
                      </div>
                    ) : (
                      <div className="bg-blue-50 p-6 rounded-lg border border-blue-200">
                        <div className="flex justify-between items-center mb-4">
                          <h4 className="text-sm font-medium text-blue-800">
                            Question {currentQuestionIndex + 1} of {followUpQuestions.length}
                          </h4>
                          <button
                            type="button"
                            className="text-blue-600 hover:text-blue-800"
                            onClick={handleHideFollowUpQuestions}
                          >
                            <FiXCircle className="h-5 w-5" />
                          </button>
                        </div>
                        
                        {(() => {
                          const question = followUpQuestions[currentQuestionIndex];
                          const items = getCompaniesAndProjectsFromResume();
                          return (
                            <div>
                              <label className="block text-sm font-medium text-blue-800 mb-3">
                                {question}
                              </label>
                              <textarea
                                rows={4}
                                className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                                placeholder="Your answer..."
                                value={followUpAnswers[currentQuestionIndex] || ''}
                                onChange={(e) => handleFollowUpAnswer(currentQuestionIndex, e.target.value)}
                              />
                              
                              {/* Company/Project Selection */}
                              <div className="mt-3">
                                <label className="block text-xs font-medium text-blue-700 mb-1">
                                  Under which company/project? (Optional)
                                </label>
                                <select
                                  className="block w-full text-sm border border-gray-300 rounded-md shadow-sm py-1 px-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                                  value={followUpCompanySelections[currentQuestionIndex] || ''}
                                  onChange={(e) => handleFollowUpCompanySelection(currentQuestionIndex, e.target.value)}
                                >
                                  <option value="">-- Select a company or project --</option>
                                  {items.map((item, itemIndex) => (
                                    <option key={`${item.type}-${itemIndex}`} value={item.displayName}>
                                      {item.type === 'company' ? '🏢 ' : '📋 '}{item.displayName}
                                    </option>
                                  ))}
                                </select>
                              </div>
                              
                              {/* Skills Option */}
                              <div className="mt-3">
                                <label className="inline-flex items-center">
                                  <input
                                    type="checkbox"
                                    className="rounded border-gray-300 text-primary-600 shadow-sm focus:border-primary-300 focus:ring focus:ring-primary-200 focus:ring-opacity-50"
                                    checked={followUpSkillsOptions[currentQuestionIndex] || false}
                                    onChange={(e) => handleFollowUpSkillsOption(currentQuestionIndex, e.target.checked)}
                                  />
                                  <span className="ml-2 text-xs text-blue-700">Add to skills section</span>
                                </label>
                              </div>
                            </div>
                          );
                        })()}
                        
                        {/* Navigation Buttons */}
                        <div className="flex justify-between mt-6">
                          <button
                            type="button"
                            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                            onClick={handlePreviousQuestion}
                            disabled={currentQuestionIndex === 0}
                          >
                            <FiArrowLeft className="-ml-1 mr-2 h-4 w-4" />
                            Back
                          </button>
                          
                          <div className="flex space-x-2">
                            {currentQuestionIndex < followUpQuestions.length - 1 ? (
                              <button
                                type="button"
                                className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                                onClick={handleNextQuestion}
                              >
                                Next
                                <FiArrowLeft className="ml-2 h-4 w-4 rotate-180" />
                              </button>
                            ) : (
                              <button
                                type="button"
                                className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                                onClick={handleHideFollowUpQuestions}
                              >
                                <FiCheck className="-ml-1 mr-2 h-4 w-4" />
                                Complete
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                    </div>
                  )}

                  {/* Free-text: combined with follow-up answers */}
                  <div>
                    <label htmlFor="additional-info" className="block text-sm font-medium text-gray-700">
                      Additional context (optional)
                    </label>
                    {followUpQuestions.length > 0 && (
                      <p className="mt-1 text-xs text-gray-500">
                        {Object.keys(followUpAnswers).filter(k => followUpAnswers[k]?.trim()).length > 0
                          ? `Included: ${Object.keys(followUpAnswers).filter(k => followUpAnswers[k]?.trim()).length} follow-up answer(s) will be sent with the text below and used to tailor your resume and cover letter.`
                          : 'Your answers to the follow-up questions above will be automatically included when you continue and used to tailor your resume and cover letter.'}
                      </p>
                    )}
                    <p className="mt-1 text-xs text-gray-600">
                      Include: concrete examples for any requirement that showed partial match above; numbers or metrics; specific tools or methods.
                    </p>
                    <textarea
                      id="additional-info"
                      name="additional-info"
                      rows={5}
                      className="mt-2 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                      placeholder="e.g. At [Company], I led a team of 5 and improved conversion by 20% using A/B tests. I have 3 years of experience with Excel for data analysis and dashboards..."
                      value={additionalInfo}
                      onChange={(e) => setAdditionalInfo(e.target.value)}
                    />
                    <p className="mt-1.5 text-xs text-gray-500">
                      Tip: 2–3 focused sentences per gap often work best.
                    </p>
                  </div>
                </div>

                {/* Navigation Buttons with Go Back and Skip options */}
                <div className="flex justify-between space-x-3">
                  <button
                    type="button"
                    className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                    onClick={() => setCurrentStep(1)}
                  >
                    Back
                  </button>
                  
                  <div className="flex space-x-3">
                    <button
                      type="button"
                      className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                      onClick={() => {
                        setAdditionalInfo(''); // Clear any entered info
                        setFollowUpAnswers({}); // Clear follow-up answers
                        setFollowUpCompanySelections({}); // Clear company selections
                        setFollowUpSelectedItems({}); // Clear selected items
                        setFollowUpSkillsOptions({}); // Clear skills options
                        setShowFollowUpQuestions(false); // Hide follow-up questions
                        setCurrentQuestionIndex(0); // Reset question index
                        setCurrentStep(3);  // Now step 3 is the previous step 4
                      }}
                    >
                      Skip Additional Info
                    </button>
                    <button
                      type="button"
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                      onClick={async () => {
                        // Save additional information if any exists
                        if (additionalInfo.trim() || Object.keys(followUpAnswers).some(key => followUpAnswers[key]?.trim())) {
                          try {
                            await jobApi.updateJobMatchAdditionalInfo(
                              jobId,
                              selectedResume,
                              additionalInfo,
                              followUpAnswers,
                              null // versionId - backend uses first version
                            );
                          } catch (error) {
                            console.error('Error saving additional information:', error);
                            // Don't block the user from proceeding if save fails
                          }
                        }
                        setCurrentStep(3);
                      }}
                    >
                      Continue to Generation
                    </button>
                  </div>
                </div>
              </div>
            )}
            
            {/* Step 3: Polish Resume & Generate Cover Letter */}
            {currentStep === 3 && (
            <div className="space-y-6">
              <h3 className="text-lg font-medium text-gray-900">Step 3: Generate Tailored Resume & Cover Letter</h3>

              <div className="bg-blue-50 p-4 rounded-md border border-blue-100 mb-4">
                <p className="text-sm text-blue-700">
                  Our AI will help you create a tailored version of your resume and generate a personalized cover letter for this job application.
                </p>
              </div>

              {/* Section (1): Cover Letter */}
              <div className="border border-gray-200 rounded-lg p-4 bg-gray-50/50">
                <h4 className="text-sm font-semibold text-gray-900 mb-3">(1) Cover Letter</h4>
                <label className="inline-flex items-center mb-3">
                  <input
                    type="checkbox"
                    className="rounded border-gray-300 text-primary-600 shadow-sm focus:border-primary-300 focus:ring focus:ring-primary-200 focus:ring-opacity-50"
                    checked={generateCoverLetter}
                    onChange={(e) => setGenerateCoverLetter(e.target.checked)}
                  />
                  <span className="ml-2">Generate Cover Letter</span>
                </label>
                {generateCoverLetter && (
                  <div className="ml-6 space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Target word count</label>
                      <input
                        type="number"
                        min={150}
                        max={800}
                        value={coverLetterWordCountInput}
                        onChange={(e) => setCoverLetterWordCountInput(e.target.value)}
                        onBlur={() => {
                          const n = Number(coverLetterWordCountInput);
                          const clamped = Math.min(800, Math.max(150, Number.isNaN(n) ? 350 : n));
                          setCoverLetterWordCount(clamped);
                          setCoverLetterWordCountInput(String(clamped));
                        }}
                        className="block w-32 rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm"
                      />
                      <p className="text-xs text-gray-500 mt-1">e.g. 300–400 words. Range: 150–800.</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Custom instructions (optional)</label>
                      <textarea
                        rows={3}
                        value={coverLetterCustomInstruction}
                        onChange={(e) => setCoverLetterCustomInstruction(e.target.value)}
                        placeholder="e.g. Mention relocation, interest in the team, or when you can start"
                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm"
                      />
                      <p className="text-xs text-gray-500 mt-1">These are added to the AI instructions for the cover letter.</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Section (2): Resume */}
              <div className="border border-gray-200 rounded-lg p-4 bg-gray-50/50">
                <h4 className="text-sm font-semibold text-gray-900 mb-3">(2) Resume</h4>
                <label className="inline-flex items-center mb-3">
                  <input
                    type="checkbox"
                    className="rounded border-gray-300 text-primary-600 shadow-sm focus:border-primary-300 focus:ring focus:ring-primary-200 focus:ring-opacity-50"
                    checked={generateResume}
                    onChange={(e) => setGenerateResume(e.target.checked)}
                  />
                  <span className="ml-2">Tailored Resume</span>
                </label>
                {generateResume && (
                  <div className="ml-6 space-y-4">
                    {/* Professional Summary */}
                    <div>
                      <div className="flex flex-wrap items-center gap-3 mb-1">
                        <label className="inline-flex items-center">
                          <input
                            type="checkbox"
                            className="rounded border-gray-300 text-primary-600 shadow-sm focus:ring-primary-500"
                            checked={selectedSections.includes('summary')}
                            onChange={(e) => {
                              if (e.target.checked) setSelectedSections((s) => s.includes('summary') ? s : [...s, 'summary']);
                              else setSelectedSections((s) => s.filter((x) => x !== 'summary'));
                            }}
                          />
                          <span className="ml-2 text-sm font-medium text-gray-700">Polish / change summary</span>
                        </label>
                        <label className="inline-flex items-center">
                          <input
                            type="checkbox"
                            className="rounded border-gray-300 text-primary-600 shadow-sm focus:ring-primary-500"
                            checked={resumeRemoveSummary}
                            onChange={(e) => setResumeRemoveSummary(e.target.checked)}
                          />
                          <span className="ml-2 text-sm font-medium text-gray-700">Remove summary</span>
                        </label>
                      </div>
                      <textarea
                        rows={3}
                        value={resumeCustomInstructions.summary}
                        onChange={(e) => setResumeCustomInstructions((prev) => ({ ...prev, summary: e.target.value }))}
                        placeholder="e.g. Preserve my wording; fix only grammar and spelling"
                        className="block w-full mt-1 rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm"
                      />
                    </div>
                    {/* Work Experience */}
                    <div>
                      <label className="inline-flex items-center mb-2">
                        <input
                          type="checkbox"
                          className="rounded border-gray-300 text-primary-600 shadow-sm focus:ring-primary-500"
                          checked={selectedSections.includes('experience')}
                          onChange={(e) => {
                            if (e.target.checked) setSelectedSections((s) => s.includes('experience') ? s : [...s, 'experience']);
                            else setSelectedSections((s) => s.filter((x) => x !== 'experience'));
                          }}
                        />
                        <span className="ml-2 text-sm font-medium text-gray-700">Polish work experience</span>
                      </label>
                      <div className="mt-2 space-y-2">
                        <p className="text-xs text-gray-500">Bullet points per job: keep all or set a max.</p>
                        {step3ResumeContent?.experience && Array.isArray(step3ResumeContent.experience) ? (
                          step3ResumeContent.experience.map((exp, idx) => {
                            const bulletCount = (exp.details && exp.details.length) || 0;
                            const limit = experienceBulletLimits[idx];
                            return (
                              <div key={idx} className="flex flex-wrap items-center gap-2 py-1 border-b border-gray-100 last:border-0">
                                <span className="text-sm text-gray-700 truncate max-w-[200px]" title={`${exp.company || ''} – ${exp.position || ''}`}>
                                  {exp.company || 'Company'} – {exp.position || 'Title'}
                                </span>
                                <span className="text-xs text-gray-500">({bulletCount} bullets)</span>
                                <select
                                  value={limit === null || limit === undefined ? 'all' : String(limit)}
                                  onChange={(e) => {
                                    const v = e.target.value;
                                    setExperienceBulletLimits((prev) => {
                                      const next = [...(prev.length ? prev : step3ResumeContent.experience.map(() => null))];
                                      next[idx] = v === 'all' ? null : parseInt(v, 10);
                                      return next;
                                    });
                                  }}
                                  className="rounded border-gray-300 text-sm shadow-sm focus:ring-primary-500"
                                >
                                  <option value="all">Keep all</option>
                                  {[3, 4, 5, 6].map((n) => (
                                    <option key={n} value={n}>Max {n}</option>
                                  ))}
                                </select>
                              </div>
                            );
                          })
                        ) : (
                          <p className="text-sm text-gray-500">Load a resume and go to Step 3 to see jobs.</p>
                        )}
                      </div>
                      <div className="mt-2">
                        <label className="block text-xs font-medium text-gray-600 mb-1">Experience filter</label>
                        <div className="flex gap-4">
                          <label className="inline-flex items-center">
                            <input
                              type="radio"
                              name="experienceFilterMode"
                              checked={experienceFilterMode === 'keep_all'}
                              onChange={() => setExperienceFilterMode('keep_all')}
                              className="border-gray-300 text-primary-600 focus:ring-primary-500"
                            />
                            <span className="ml-2 text-sm text-gray-700">Keep all experiences</span>
                          </label>
                          <label className="inline-flex items-center">
                            <input
                              type="radio"
                              name="experienceFilterMode"
                              checked={experienceFilterMode === 'ai_relevant_only'}
                              onChange={() => setExperienceFilterMode('ai_relevant_only')}
                              className="border-gray-300 text-primary-600 focus:ring-primary-500"
                            />
                            <span className="ml-2 text-sm text-gray-700">Let AI include only relevant (may omit some)</span>
                          </label>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">Some jobs may be omitted to focus on fit.</p>
                      </div>
                      <textarea
                        rows={3}
                        value={resumeCustomInstructions.experience}
                        onChange={(e) => setResumeCustomInstructions((prev) => ({ ...prev, experience: e.target.value }))}
                        placeholder="e.g. Keep facts unchanged; only improve clarity and grammar. Do not invent metrics."
                        className="block w-full mt-2 rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm"
                      />
                    </div>
                    {/* Skills */}
                    <div>
                      <label className="inline-flex items-center mb-1">
                        <input
                          type="checkbox"
                          className="rounded border-gray-300 text-primary-600 shadow-sm focus:ring-primary-500"
                          checked={selectedSections.includes('skills')}
                          onChange={(e) => {
                            if (e.target.checked) setSelectedSections((s) => s.includes('skills') ? s : [...s, 'skills']);
                            else setSelectedSections((s) => s.filter((x) => x !== 'skills'));
                          }}
                        />
                        <span className="ml-2 text-sm font-medium text-gray-700">Polish skills</span>
                      </label>
                      <textarea
                        rows={3}
                        value={resumeCustomInstructions.skills}
                        onChange={(e) => setResumeCustomInstructions((prev) => ({ ...prev, skills: e.target.value }))}
                        placeholder="e.g. Keep the same order; only refine phrasing. Do not add skills not on my resume."
                        className="block w-full mt-1 rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm"
                      />
                    </div>
                  </div>
                )}
              </div>

              <CreditCostNotification
                serviceCost={calculateCost()}
                serviceName={getServiceName()}
              />

              <div className="flex justify-between space-x-3">
                <button
                  type="button"
                  className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  onClick={() => setCurrentStep(2)}
                >
                  <FiArrowLeft className="-ml-1 mr-2 h-4 w-4" />
                  Back
                </button>

                <div className="flex space-x-3">
                  <button
                    type="button"
                    className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                    onClick={handleSkipToApply}
                  >
                    <FiSkipForward className="-ml-1 mr-2 h-4 w-4" />
                    Skip to Apply
                  </button>

                  <DisabledCreditButton
                    serviceType={getServiceType()}
                    onClick={handleGenerate}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                    disabled={polishing || (!generateResume && !generateCoverLetter)}
                  >
                    {polishing ? (
                      <>
                        <FiLoader className="animate-spin -ml-1 mr-2 h-4 w-4" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <FiCpu className="-ml-1 mr-2 h-4 w-4" />
                        {getButtonText()} ({calculateCost()} credits)
                      </>
                    )}
                  </DisabledCreditButton>
                </div>
              </div>
            </div>
            )}
            
            {/* Step 4: Review & Compare */}
            {currentStep === 4 && (
              <div className="space-y-6">
                <h3 className="text-lg font-medium text-gray-900">Step 4: Review Generated Resume & Cover Letter</h3>
                
                <div className="bg-green-50 p-4 rounded-md border border-green-100">
                  <div className="flex">
                    <FiCheckCircle className="h-5 w-5 text-green-500 mt-0.5 mr-2" />
                    <div>
                      <p className="text-sm text-green-700">
                        Your resume has been tailored and a personalized cover letter has been generated.
                        Review and edit them before proceeding to the application.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Tabs Navigation - Compare Changes and Cover Letter only */}
                <div className="border-b border-gray-200">
                  <nav className="-mb-px flex space-x-8">
                    {generateResume && (
                      <button
                        className={`${
                          activeTab === 1
                            ? 'border-primary-500 text-primary-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                        onClick={() => setActiveTab(1)}
                      >
                        <FiColumns className="inline-block mr-2 h-4 w-4" />
                        Compare Changes
                      </button>
                    )}
                    <button
                      className={`${
                        activeTab === 2
                          ? 'border-primary-500 text-primary-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                      onClick={() => setActiveTab(2)}
                    >
                      <FiMail className="inline-block mr-2 h-4 w-4" />
                      Cover Letter
                    </button>
                  </nav>
                </div>
                
                {/* Tab Content - Compare Changes (only shown if resume was polished) */}
                {activeTab === 1 && generateResume && (
                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    {originalResumeContent && polishedResumeContent ? (
                      <>
                        <div className="mb-4 flex justify-end space-x-3">
                          {generateResume && polishedResumeContent && (
                            <button
                              type="button"
                              onClick={() => setShowLastMinuteModal(true)}
                              className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                            >
                              <FiEdit className="-ml-0.5 mr-2 h-4 w-4" />
                              Last minute changes
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => setShowResumeStyleModal(true)}
                            className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                          >
                            <FiDownload className="-ml-0.5 mr-2 h-4 w-4" />
                            Download Resume PDF
                          </button>
                        </div>
                        <ResumeComparison 
                          originalResumeContent={originalResumeContent} 
                          polishedResumeContent={polishedResumeContent} 
                        />
                      </>
                    ) : (
                      <LoadingSpinner size="small" message="Loading resume content..." />
                    )}
                  </div>
                )}

                {/* Tab Content - Cover Letter Editor */}
                {activeTab === 2 && (
                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center space-y-4 lg:space-y-0">
                    <div>
                    <h1 className="text-2xl font-bold text-gray-900">Edit Cover Letter</h1>
                    </div>
                    <div className="flex space-x-3">
                          <button
                            type="button"
                            onClick={openCoverLetterDownloadModal}
                            className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                            disabled={!generateCoverLetter && !generatedCoverLetter}
                            >
                            <FiDownload className="-ml-1 mr-2 h-4 w-4" />
                            Download PDF
                          </button>
                          
                          <button
                            type="button"
                            className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                            onClick={handleSaveCoverLetterEdits}
                          >
                            <FiSave className="-ml-1 mr-2 h-4 w-4" />
                            Save Cover Letter Changes
                          </button>
                    </div>
                    </div>
                    {generateCoverLetter && generatedCoverLetter ? (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Cover letter</label>
                        <textarea
                          rows={18}
                          className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md font-sans"
                          value={getCoverLetterFullText()}
                          onChange={(e) => handleCoverLetterFullTextChange(e.target.value)}
                          placeholder={'Dear Hiring Manager,\n\nI am writing to express my interest in...'}
                        />
                        <p className="mt-1 text-xs text-gray-500">Edit your full cover letter here. Use line breaks between paragraphs.</p>
                      </div>
                    ) : !generateCoverLetter ? (
                      <div>
                        <p className="text-sm text-gray-600 mb-3">
                          You didn't select to generate a cover letter. You can write one below.
                        </p>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Cover letter</label>
                        <textarea
                          rows={18}
                          className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md font-sans"
                          value={getCoverLetterFullText()}
                          onChange={(e) => handleCoverLetterFullTextChange(e.target.value)}
                          placeholder={'Dear Hiring Manager,\n\nI am writing to express my interest in the position...'}
                        />
                        <p className="mt-1 text-xs text-gray-500">Use line breaks between paragraphs.</p>
                      </div>
                    ) : (
                      <LoadingSpinner size="small" message="Loading cover letter..." />
                    )}
                  </div>
                )}
                
                <div className="flex justify-between space-x-3">
                  <button
                    type="button"
                    className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                    onClick={() => setCurrentStep(3)}
                  >
                    <FiArrowLeft className="-ml-1 mr-2 h-4 w-4" />
                    Back
                  </button>
                  <button
                    type="button"
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                    onClick={() => setCurrentStep(5)}
                  >
                    Continue to Apply
                  </button>
                </div>
              </div>
            )}
            
            {/* Step 5: Apply & Track */}
            {currentStep === 5 && (
              <div className="space-y-6">
                <h3 className="text-lg font-medium text-gray-900">Step 5: Apply for the Job</h3>
                
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  {job.sourceUrl ? (
                    <div className="text-center">
                      <h4 className="text-base font-medium text-gray-800 mb-4">Apply on Company Website</h4>
                      <p className="text-sm text-gray-600 mb-4">
                        This job has an external application link. Download your tailored resume and cover letter, 
                        then apply directly on the company website.
                      </p>
                      <div className="flex flex-col sm:flex-row justify-center gap-3 mb-6">
                        <button
                          type="button"
                          onClick={() => setShowResumeStyleModal(true)}
                          className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                        >
                          <FiDownload className="-ml-1 mr-2 h-4 w-4" />
                          Download Resume
                        </button>
                        {selectedCoverLetter && (
                          <button
                            type="button"
                            onClick={openCoverLetterDownloadModal}
                            className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                          >
                            <FiDownload className="-ml-1 mr-2 h-4 w-4" />
                            Download Cover Letter
                          </button>
                        )}
                      </div>
                      <a
                        href={job.sourceUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                      >
                        <FiExternalLink className="-ml-1 mr-2 h-4 w-4" />
                        Apply on Company Website
                      </a>
                    </div>
                  ) : (
                    <div className="text-center">
                      <h4 className="text-base font-medium text-gray-800 mb-4">No Application Link Available</h4>
                      <p className="text-sm text-gray-600 mb-4">
                        You did not provide any job link. Please use the downloaded PDF to apply on website.
                      </p>
                      <div className="flex flex-col sm:flex-row justify-center gap-3 mb-6">
                        <button
                          type="button"
                          onClick={() => setShowResumeStyleModal(true)}
                          className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                        >
                          <FiDownload className="-ml-1 mr-2 h-4 w-4" />
                          Download Resume
                        </button>
                        {selectedCoverLetter && (
                          <button
                            type="button"
                            onClick={openCoverLetterDownloadModal}
                            className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                          >
                            <FiDownload className="-ml-1 mr-2 h-4 w-4" />
                            Download Cover Letter
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                
                <div className="border-t border-gray-200 pt-6">
                  <h4 className="text-base font-medium text-gray-800 mb-4">Track This Application</h4>
                  <p className="text-sm text-gray-600 mb-4">
                    Keep track of this application in your dashboard to monitor its status and set follow-up reminders.
                  </p>
                  
                  <div>
                    <label htmlFor="notes" className="block text-sm font-medium text-gray-700">
                      Application Notes (Optional)
                    </label>
                    <textarea
                      id="notes"
                      name="notes"
                      rows={3}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                      placeholder="Add any notes about this application..."
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                    />
                  </div>
                  
                  <div className="flex justify-between mt-6">
                    <button
                      type="button"
                      className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                      onClick={() => setCurrentStep(4)}
                    >
                      <FiArrowLeft className="-ml-1 mr-2 h-4 w-4" />
                      Back
                    </button>
                    <button
                      type="button"
                      className="inline-flex justify-center items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                      onClick={handleSaveApplication}
                      disabled={submitting || !selectedResume}
                    >
                      {submitting ? (
                        <>
                          <FiLoader className="animate-spin -ml-1 mr-2 h-4 w-4" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <FiSave className="-ml-1 mr-2 h-4 w-4" />
                          Track This Application
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Cover letter download modal - rename before download */}
      {showCoverLetterDownloadModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto" aria-modal="true" role="dialog">
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" onClick={() => !coverLetterPdfDownloading && setShowCoverLetterDownloadModal(false)} />
            <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-2">Download Cover Letter</h3>
              <p className="text-sm text-gray-500 mb-3">Edit the filename below if you want, then download.</p>
              <input
                type="text"
                value={coverLetterDownloadFileName}
                onChange={(e) => setCoverLetterDownloadFileName(e.target.value)}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm mb-4"
                placeholder="Name - Cover Letter for Job.pdf"
                disabled={coverLetterPdfDownloading}
              />
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => !coverLetterPdfDownloading && setShowCoverLetterDownloadModal(false)}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                  disabled={coverLetterPdfDownloading}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => generateCoverLetterPDF(coverLetterDownloadFileName)}
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
                  disabled={coverLetterPdfDownloading}
                >
                  {coverLetterPdfDownloading ? 'Downloading...' : 'Download'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Resume Style Modal */}
      <ResumeStyleModal
        show={showResumeStyleModal}
        onClose={() => setShowResumeStyleModal(false)}
        resumeId={selectedResume}
        versionId={polishedResumeContent?.versionId || null}
        suggestedFileName={job ? getSuggestedResumeFileName() : null}
        job={job}
        resumeContent={polishedResumeContent || originalResumeContent}
      />

      {/* Last minute changes modal - edit polished resume; Save or Discard only */}
      {showLastMinuteModal && polishedResumeContent && (
        <div className="fixed inset-0 z-50 overflow-y-auto" aria-modal="true" role="dialog">
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" />
            <div className="relative bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] flex flex-col">
              <div className="px-4 py-3 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Last minute changes</h3>
                <p className="text-sm text-gray-500 mt-0.5">Edit your polished resume. Save or Discard to close.</p>
              </div>
              <div className="flex-1 overflow-auto p-4 min-h-0">
                <ResumeDocument
                  ref={lastMinuteEditorRef}
                  resumeId={polishedResumeContent.resumeId || selectedResume}
                  versionId={polishedResumeContent.versionId}
                  isEmbedded={true}
                  onSaveSuccess={async () => {
                    try {
                      const resumeId = polishedResumeContent.resumeId || selectedResume;
                      const versionId = polishedResumeContent.versionId;
                      const res = await resumeApi.getResumeVersion(resumeId, versionId);
                      if (res?.data) {
                        setPolishedResumeContent({
                          ...res.data.content,
                          versionId: res.data.versionId,
                          resumeId: res.data.resumeId,
                        });
                      }
                    } catch (err) {
                      console.error('Error refetching version after save:', err);
                      toast.error('Saved but failed to refresh comparison');
                    }
                    setShowLastMinuteModal(false);
                  }}
                />
              </div>
              <div className="px-4 py-3 border-t border-gray-200 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowLastMinuteModal(false)}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  Discard
                </button>
                <button
                  type="button"
                  onClick={() => lastMinuteEditorRef.current?.save()}
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  <FiSave className="-ml-1 mr-2 h-4 w-4" />
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JobApplication;